import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Bot, MessageSquare, Users, Trash2, ChevronDown, ChevronUp, MessageCircle, Send, Tag, HelpCircle, Loader2, Plus } from "lucide-react";
import { API, auth, handleAuthError, fmtDate, ghostBtn, primaryBtn, inputClass, labelClass } from "./shared";
import { useConfirm } from "./useConfirm";
import { useSettings } from "@/context/SettingsContext";
import { useLanguage } from "@/context/LanguageContext";

const Kpi = ({ icon: Icon, label, value, testid }) => (
  <div className="rounded-2xl border border-brand/10 bg-white p-5" data-testid={testid}>
    <p className="inline-flex items-center gap-2 font-mono text-[10px] uppercase tracking-widest text-brand"><Icon className="h-3.5 w-3.5 text-gold" />{label}</p>
    <p className="font-display mt-2 text-3xl font-bold text-forest">{value}</p>
  </div>
);

const AdminAiChats = ({ token, logout }) => {
  const [data, setData] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [onlyIdentified, setOnlyIdentified] = useState(false);
  const [faq, setFaq] = useState(null);
  const [faqLoading, setFaqLoading] = useState("");
  const [publishing, setPublishing] = useState(false);
  const { settings, refresh } = useSettings();
  const { t } = useLanguage();
  const { confirm, dialog } = useConfirm();
  const load = () => axios.get(`${API}/ai/chats`, auth(token)).then((r) => setData(r.data)).catch((err) => handleAuthError(err, logout, () => toast.error("Chargement impossible")));
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const suggestFaq = async (theme) => {
    setFaqLoading(theme);
    try {
      const r = await axios.post(`${API}/ai/faq-suggest`, { theme }, auth(token));
      setFaq({ theme, q: r.data.q, a: r.data.a, questions: r.data.questions });
    } catch (err) {
      const d = err.response?.data?.detail;
      handleAuthError(err, logout, () => toast.error(typeof d === "string" ? d : "L'assistant IA est indisponible"));
    } finally {
      setFaqLoading("");
    }
  };
  const publishFaq = async () => {
    if (!faq?.q.trim() || !faq?.a.trim()) return toast.error("Question et réponse obligatoires");
    setPublishing(true);
    try {
      const current = settings.content?.faq?.length ? settings.content.faq : t.faq.items.map((x) => ({ q: x.q, a: x.a }));
      await axios.put(`${API}/settings`, { ...settings, content: { ...settings.content, faq: [...current, { q: faq.q.trim(), a: faq.a.trim() }] } }, auth(token));
      await refresh();
      toast.success("Question ajoutée à la FAQ du site");
      setFaq(null);
    } catch (err) {
      handleAuthError(err, logout, () => toast.error("Échec de la publication"));
    } finally {
      setPublishing(false);
    }
  };

  const remove = async (c) => {
    if (!(await confirm({ title: "Supprimer cette conversation ?", description: "Les messages seront définitivement effacés." }))) return;
    try { await axios.delete(`${API}/ai/chats/${c.session_id}`, auth(token)); toast.success("Conversation supprimée"); load(); }
    catch (err) { handleAuthError(err, logout, () => toast.error("Échec de la suppression")); }
  };
  const followupEmail = async (v) => {
    try { await axios.post(`${API}/visitors/${v.visitor_id}/followup`, {}, auth(token)); toast.success(`Relance envoyée par e-mail à ${v.email}`); load(); }
    catch (err) { handleAuthError(err, logout, () => toast.error(err.response?.data?.detail || "Échec de l'envoi")); }
  };
  const followupWhatsapp = async (v) => {
    const text = `Bonjour ${v.name}, merci pour votre visite sur le site de Groupe Auto Luxe. Vous avez posé une question à notre assistant : notre équipe est à votre disposition pour y répondre en détail, le devis est gratuit et sans engagement. À bientôt !`;
    window.open(`https://wa.me/${v.phone.replace(/[^\d]/g, "")}?text=${encodeURIComponent(text)}`, "_blank", "noopener");
    try { await axios.post(`${API}/visitors/${v.visitor_id}/followup-whatsapp`, {}, auth(token)); load(); } catch { /* noop */ }
  };

  if (!data) return <p className="text-sm text-brand">Chargement...</p>;
  const chats = onlyIdentified ? data.chats.filter((c) => c.visitor) : data.chats;
  const maxTheme = Math.max(1, ...data.themes.map((x) => x.count));

  return (
    <div className="space-y-6" data-testid="admin-ai-chats-tab">
      {dialog}
      <div className="grid gap-4 sm:grid-cols-3">
        <Kpi icon={MessageSquare} label="Conversations" value={data.total} testid="ai-chats-kpi-total" />
        <Kpi icon={Bot} label="30 derniers jours" value={data.last_30_days} testid="ai-chats-kpi-30d" />
        <Kpi icon={Users} label="Visiteurs identifiés" value={data.identified} testid="ai-chats-kpi-identified" />
      </div>

      <section className="rounded-3xl border border-brand/10 bg-white p-6" data-testid="ai-chats-themes">
        <h3 className="font-display flex items-center gap-2 text-base font-bold text-forest"><Tag className="h-4 w-4 text-gold" /> Thèmes les plus demandés (30 jours)</h3>
        <p className="mt-1 text-xs text-brand-hover">Calculé automatiquement à partir des questions posées à l'assistant : utile pour enrichir la FAQ ou préparer des offres.</p>
        {data.themes.length === 0 ? <p className="mt-4 text-sm text-brand">Aucune question sur la période.</p> : (
          <ul className="mt-4 space-y-2">
            {data.themes.map((th, i) => (
              <li key={th.theme} className="flex items-center gap-3" data-testid={`ai-chats-theme-${i}`}>
                <span className="w-44 shrink-0 text-sm text-forest">{th.theme}</span>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-mint"><div className="h-full rounded-full bg-gold" style={{ width: `${(th.count / maxTheme) * 100}%` }} /></div>
                <span className="w-8 text-right font-mono text-xs text-brand">{th.count}</span>
                <button type="button" onClick={() => suggestFaq(th.theme)} disabled={!!faqLoading} data-testid={`ai-chats-theme-${i}-faq`} className={`${ghostBtn} shrink-0 px-3 py-1.5`} title="Rédiger une question de FAQ avec l'IA à partir de ce thème">
                  {faqLoading === th.theme ? <Loader2 className="h-3 w-3 animate-spin" /> : <HelpCircle className="h-3 w-3" />} FAQ
                </button>
              </li>
            ))}
          </ul>
        )}
        {faq && (
          <div className="mt-6 rounded-2xl border border-gold/30 bg-cream p-5" data-testid="ai-chats-faq-draft">
            <p className="font-mono text-[10px] uppercase tracking-widest text-gold">Proposition pour la FAQ · {faq.theme}</p>
            {faq.questions?.length > 0 && <p className="mt-1 text-xs text-brand-hover">Basée sur {faq.questions.length} question{faq.questions.length > 1 ? "s" : ""} réelle{faq.questions.length > 1 ? "s" : ""} de visiteurs, ex. « {faq.questions[0]} »</p>}
            <label className={`${labelClass} mt-4`}>Question</label>
            <input value={faq.q} onChange={(e) => setFaq({ ...faq, q: e.target.value })} data-testid="ai-chats-faq-q" className={inputClass} />
            <label className={`${labelClass} mt-3`}>Réponse</label>
            <textarea rows={4} value={faq.a} onChange={(e) => setFaq({ ...faq, a: e.target.value })} data-testid="ai-chats-faq-a" className={`${inputClass} resize-none`} />
            <div className="mt-4 flex flex-wrap gap-2">
              <button type="button" onClick={publishFaq} disabled={publishing} data-testid="ai-chats-faq-publish" className={primaryBtn}><Plus className="h-4 w-4" /> {publishing ? "Publication..." : "Ajouter à la FAQ du site"}</button>
              <button type="button" onClick={() => setFaq(null)} data-testid="ai-chats-faq-cancel" className={ghostBtn}>Annuler</button>
            </div>
          </div>
        )}
      </section>

      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="font-display text-base font-bold text-forest">Conversations ({chats.length})</h3>
        <button type="button" onClick={() => setOnlyIdentified((v) => !v)} data-testid="ai-chats-filter-identified" aria-pressed={onlyIdentified} className={`${ghostBtn} ${onlyIdentified ? "border-gold text-gold" : ""}`}><Users className="h-3.5 w-3.5" /> Visiteurs identifiés uniquement</button>
      </div>

      {chats.length === 0 && <p className="rounded-2xl border border-dashed border-brand/20 p-8 text-center text-sm text-brand" data-testid="ai-chats-empty">Aucune conversation pour le moment.</p>}
      <ul className="space-y-3">
        {chats.map((c, i) => {
          const open = openId === c.session_id;
          const first = c.messages.find((m) => m.role === "user")?.text || "";
          return (
            <li key={c.session_id} className="rounded-2xl border border-brand/10 bg-white" data-testid={`ai-chat-row-${i}`}>
              <button type="button" onClick={() => setOpenId(open ? null : c.session_id)} data-testid={`ai-chat-row-${i}-toggle`} className="flex w-full items-start justify-between gap-4 p-5 text-left">
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold text-forest">{first || "(vide)"}</p>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-widest text-brand">
                    {fmtDate(c.updated_at)} · {c.user_messages} question{c.user_messages > 1 ? "s" : ""} · {c.lang === "en" ? "EN" : "FR"}{c.path ? ` · ${c.path}` : ""}
                    {c.visitor && <span className="ml-2 rounded-full bg-mint px-2 py-0.5 text-forest" data-testid={`ai-chat-row-${i}-visitor`}>{c.visitor.name}</span>}
                  </p>
                </div>
                {open ? <ChevronUp className="h-4 w-4 shrink-0 text-brand" /> : <ChevronDown className="h-4 w-4 shrink-0 text-brand" />}
              </button>
              {open && (
                <div className="border-t border-brand/10 p-5" data-testid={`ai-chat-row-${i}-messages`}>
                  <div className="space-y-2">
                    {c.messages.map((m, k) => (
                      <div key={k} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                        <p className={`max-w-[80%] whitespace-pre-line rounded-2xl px-4 py-2 text-sm leading-relaxed ${m.role === "user" ? "bg-gold text-white" : "bg-cream text-forest"}`}>{m.text}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-4 flex flex-wrap items-center gap-2">
                    {c.visitor ? (
                      <>
                        <span className="text-xs text-forest">{c.visitor.name}{c.visitor.phone ? ` · ${c.visitor.phone}` : ""}{c.visitor.email ? ` · ${c.visitor.email}` : ""}</span>
                        {c.visitor.phone && <button onClick={() => followupWhatsapp(c.visitor)} data-testid={`ai-chat-row-${i}-followup-whatsapp`} className={`${ghostBtn} px-3 py-1.5`}><MessageCircle className="h-3 w-3" /> Relancer WhatsApp</button>}
                        {c.visitor.email && <button onClick={() => followupEmail(c.visitor)} data-testid={`ai-chat-row-${i}-followup-email`} className={`${ghostBtn} px-3 py-1.5`}><Send className="h-3 w-3" /> Relancer e-mail</button>}
                        {c.visitor.followup_at && <span className="font-mono text-[10px] uppercase tracking-widest text-gold">Relancé {fmtDate(c.visitor.followup_at)}</span>}
                      </>
                    ) : <span className="text-xs text-brand-hover">Visiteur anonyme (n'a rempli aucun formulaire)</span>}
                    <button onClick={() => remove(c)} data-testid={`ai-chat-row-${i}-delete`} className={`${ghostBtn} ml-auto px-3 py-1.5 hover:border-red-300 hover:text-red-600`}><Trash2 className="h-3 w-3" /> Supprimer</button>
                  </div>
                </div>
              )}
            </li>
          );
        })}
      </ul>
    </div>
  );
};

export default AdminAiChats;
